import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { queryKeys } from '../lib/queryKeys';
import type { CreateCategoryRequest, ReorderCategoriesRequest, UpdateCategoryRequest } from '../types/tickets';

// Pickers (the default) get active categories only. Category management, list filters, reports and anything that must
// resolve a category already on a ticket pass includeArchived. Both variants sit under queryKeys.categories.list(orgId),
// so the mutations below refresh them together.
export function categoriesQueryKey(organizationId?: string, includeArchived = false) {
  return queryKeys.categories.listVariant(organizationId, includeArchived);
}

/** The organization's ticket categories as a tree in display order (each followed by its subcategories) */
export function useCategories(organizationId?: string, options?: { includeArchived?: boolean }) {
  const includeArchived = options?.includeArchived ?? false;
  return useQuery({
    queryKey: categoriesQueryKey(organizationId, includeArchived),
    queryFn: async () => {
      const response = await apiService.getCategories(organizationId, { includeArchived });
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch categories'));
      }
      return Array.isArray(response.data) ? response.data : [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!organizationId, // Don't fetch until organizationId is set
  });
}

export function useCategory(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.categories.detail(id ?? ''),
    queryFn: async () => {
      if (!id) throw new Error('Category ID is required');

      const response = await apiService.getCategory(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch category'));
      }
      return response.data;
    },
    enabled: !!id,
  });
}

// A category change can rename what tickets show (paths) and what reports group by
function invalidateAfterCategoryChange(queryClient: QueryClient, organizationId?: string, ticketsToo = false) {
  queryClient.invalidateQueries({ queryKey: queryKeys.categories.list(organizationId) });
  queryClient.invalidateQueries({ queryKey: [...queryKeys.reports.all, 'categories'] });
  if (ticketsToo) {
    queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
  }
}

export function useCreateCategory(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (category: CreateCategoryRequest) => {
      const response = await apiService.createCategory(category, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to create category'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCategoryChange(queryClient, organizationId),
  });
}

export function useUpdateCategory(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateCategoryRequest }) => {
      const response = await apiService.updateCategory(id, updates, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update category'));
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // A rename or move changes the path tickets show
      invalidateAfterCategoryChange(queryClient, organizationId, true);
      queryClient.invalidateQueries({ queryKey: queryKeys.categories.detail(variables.id) });
    },
  });
}

export function useReorderCategories(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ReorderCategoriesRequest) => {
      const response = await apiService.reorderCategories(request, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to reorder categories'));
      }
      return response.data;
    },
    onSettled: () => invalidateAfterCategoryChange(queryClient, organizationId),
  });
}

export function useArchiveCategory(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.archiveCategory(id, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to archive category'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCategoryChange(queryClient, organizationId),
  });
}

export function useRestoreCategory(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.restoreCategory(id, organizationId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to restore category'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCategoryChange(queryClient, organizationId),
  });
}

// Permanently deletes a category nothing uses
export function useDeleteCategory(organizationId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.deleteCategory(id, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete category'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCategoryChange(queryClient, organizationId),
  });
}
